/* PlanPicker — the pricing plan cards, ported from the "JAI Onboarding" design
   prototype. Renders whatever is in config/plans.js. Reuses the app's brand
   tokens (via landingShared), the mascot, and the auth CheckIcon/PrimaryButton. */

import { useState } from 'react'
import { JAI, FONTS } from '../landing/landingShared'
import { JAIMascot } from '../JAIMascot'
import { CheckIcon, PrimaryButton } from '../AuthFields'
import { PLANS, planPrice, fmtPrice, annualMonthlyEquivalent, annualSavingsLabel, CREDITS_EXPLAINER } from '../../config/plans'

// ── What each card offers a given subscriber ─────────────────
// Plans form a ladder, so "is this an upgrade?" is a comparison of positions
// on it. Read from PLANS rather than hardcoded, so adding a tier to that file
// slots into the ordering here automatically.
const PLAN_RANK = Object.fromEntries(PLANS.map((p, i) => [p.id, i]))

/**
 * Decide how one plan card should behave for the person looking at it.
 *
 * The rule that drives all of this: nobody should be offered the exact thing
 * they are already paying for. A Pro monthly subscriber sees Pro annual as a
 * switch and Elite as an upgrade, but the Pro monthly card is spent — clicking
 * it could only ever send them to Stripe to buy their own subscription again.
 *
 * Moving down a tier (or annual -> monthly) is offered too, but it is not an
 * instant swap: it is scheduled for the next renewal, so the user keeps what
 * they've paid for. The confirm dialog (PlanChangeDialog) spells that out
 * before anything happens. Only free Starter stays unselectable here, because
 * going to Starter is cancelling, not changing plan.
 *
 *   currentPlan     — 'starter' | 'pro' | 'elite' | null (null = not subscribed)
 *   currentInterval — 'monthly' | 'annual' | null
 *   billing         — the cycle the page is currently showing
 *   hasScheduledChange — a downgrade is queued; re-picking the current plan cancels it
 */
function planCardState(plan, { currentPlan, currentInterval, billing, hasScheduledChange }) {
  const subscribed = !!currentPlan && currentPlan !== 'starter'
  if (!subscribed) return { disabled: false, cta: plan.cta, note: '' }

  const here = PLAN_RANK[plan.id] ?? 0
  const theirs = PLAN_RANK[currentPlan] ?? 0

  if (plan.id === currentPlan) {
    // Same tier: only the billing cycle can still be worth changing. When the
    // interval is unknown (an older subscription that predates us recording
    // it), treat the tier as settled rather than guessing and offering a
    // "switch" that might be to the cycle they're already on.
    if (!currentInterval) {
      return { disabled: true, cta: 'Your current plan', note: 'Manage billing in Settings.' }
    }
    if (billing === currentInterval) {
      if (hasScheduledChange) {
        return {
          disabled: false,
          cta: 'Keep this plan',
          note: 'Cancels your scheduled plan change. Nothing is charged.',
        }
      }
      return { disabled: true, cta: 'Your current plan', note: '' }
    }
    return {
      disabled: false,
      cta: billing === 'annual' ? 'Switch to annual billing' : 'Switch to monthly billing',
      note: '',
    }
  }

  if (here > theirs) {
    return { disabled: false, cta: `Upgrade to ${plan.name}`, note: '' }
  }

  // Below their tier. Free Starter is cancelling, not a plan change, so it
  // keeps pointing at Settings -> Cancel plan.
  if (plan.free) {
    return {
      disabled: true,
      cta: `${plan.name} is below your plan`,
      note: 'To move to the free plan, cancel in Settings — you keep your current plan until the period you have paid for ends.',
    }
  }
  return {
    disabled: false,
    cta: `Switch to ${plan.name}`,
    note: 'Takes effect at your next renewal. Nothing is charged today.',
  }
}

// The "Most popular" plan's own savings figure — computed from its actual
// prices (see annualSavingsLabel in config/plans.js), not a flat guess.
const HEADLINE_SAVINGS_PLAN = PLANS.find((p) => p.badge) || PLANS.find((p) => !p.free)

// ── Billing toggle (monthly / annual pill) ──────────────────
function BillingToggle({ billing, onChange }) {
  const opts = [{ id: 'monthly', label: 'Monthly' }, { id: 'annual', label: 'Annual' }]
  const savingsLabel = HEADLINE_SAVINGS_PLAN ? annualSavingsLabel(HEADLINE_SAVINGS_PLAN) : null
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <div style={{ display: 'inline-flex', gap: 3, border: `1px solid ${JAI.hairSoft}`, borderRadius: 999, padding: 4 }}>
        {opts.map((o) => {
          const active = billing === o.id
          return (
            <button key={o.id} type="button" onClick={() => onChange(o.id)}
              style={{ border: 'none', cursor: 'pointer', borderRadius: 999, padding: '7px 16px', fontSize: 13, fontWeight: 600, fontFamily: FONTS.tight, transition: 'all 0.18s', background: active ? JAI.white : 'transparent', color: active ? JAI.navy : JAI.mute, boxShadow: active ? '0 2px 8px rgba(10,31,68,0.10)' : 'none' }}>
              {o.label}
            </button>
          )
        })}
      </div>
      {savingsLabel && (
        <span style={{ fontSize: 11, fontWeight: 700, fontFamily: FONTS.tight, letterSpacing: '0.02em', color: billing === 'annual' ? JAI.success : JAI.faint, padding: 0, transition: 'all 0.2s' }}>
          {savingsLabel}
        </span>
      )}
    </div>
  )
}

// ── Feature bullet ──────────────────────────────────────────
function FeatureLine({ children, muted }) {
  return (
    <li style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
      <span style={{ width: 17, height: 17, borderRadius: 6, flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CheckIcon size={10} stroke={JAI.blue} sw={2.8} />
      </span>
      <span style={{ fontSize: 13, color: muted ? JAI.faint : JAI.mute, fontFamily: FONTS.tight, lineHeight: 1.45 }}>{children}</span>
    </li>
  )
}

function PriceBlock({ plan, billing, size = 34 }) {
  // Annual billing shows BOTH numbers: the monthly-equivalent large (what it
  // "costs per month" to think about), and the real yearly total charged
  // directly beneath it in smaller type. Never show only the
  // monthly-equivalent — that alone would misstate what's actually charged.
  if (!plan.free && billing === 'annual') {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: size, fontWeight: 800, letterSpacing: '-0.03em', color: JAI.navy, fontFamily: FONTS.tight }}>{fmtPrice(annualMonthlyEquivalent(plan))}</span>
          <span style={{ fontSize: 12.5, color: JAI.faint, fontFamily: FONTS.tight }}>/ month</span>
        </div>
        <div style={{ fontSize: 12, color: JAI.faint, fontFamily: FONTS.tight, marginTop: 2 }}>{fmtPrice(plan.annualTotal)} billed once yearly</div>
      </div>
    )
  }
  const price = planPrice(plan, billing)
  const suffix = plan.free ? 'forever' : '/ month'
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
      <span style={{ fontSize: size, fontWeight: 800, letterSpacing: '-0.03em', color: JAI.navy, fontFamily: FONTS.tight }}>{fmtPrice(price)}</span>
      <span style={{ fontSize: 12.5, color: JAI.faint, fontFamily: FONTS.tight }}>{suffix}</span>
    </div>
  )
}

// ── Secondary (outline) button ──────────────────────────────
function SecondaryButton({ children, onClick, loading }) {
  const [hover, setHover] = useState(false)
  return (
    <button type="button" onClick={onClick} disabled={loading}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ width: '100%', background: JAI.white, color: JAI.blue, fontWeight: 650, borderRadius: 10, padding: '11px 16px', fontSize: 14, border: `1.5px solid ${hover ? JAI.blue : 'rgba(30,91,214,0.45)'}`, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1, transition: 'all 0.18s', fontFamily: FONTS.tight, letterSpacing: '-0.01em' }}>
      {children}
    </button>
  )
}

// ── "Current plan" ribbon ───────────────────────────────────
function CurrentPlanBadge() {
  return (
    <span style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: JAI.navy, color: 'white', fontSize: 11, fontWeight: 700, fontFamily: FONTS.tight, letterSpacing: '0.04em', borderRadius: 999, padding: '5px 13px', whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(10,31,68,0.30)' }}>
      Current plan
    </span>
  )
}

// ── Disabled (unavailable) button ───────────────────────────
function DisabledButton({ children }) {
  return (
    <button type="button" disabled aria-disabled="true"
      style={{ width: '100%', color: JAI.faint, fontWeight: 650, borderRadius: 10, padding: '11px 16px', fontSize: 14, border: `1px solid ${JAI.hairSoft}`, cursor: 'not-allowed', fontFamily: FONTS.tight, letterSpacing: '-0.01em' }}>
      {children}
    </button>
  )
}

// ── Plan card ───────────────────────────────────────────────
function PlanCard({ plan, billing, highlighted, onSelect, loading, state, isCurrent }) {
  const [hover, setHover] = useState(false)
  const disabled = state.disabled
  // A card they can't act on shouldn't compete for attention with the ones
  // they can — but it stays fully readable, because "what am I already
  // getting?" is exactly the comparison this page exists to support.
  const lift = hover && !highlighted && !disabled
  return (
    <div
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ position: 'relative', display: 'flex', flexDirection: 'column', background: JAI.white, borderRadius: 18, border: isCurrent ? `1.5px solid ${JAI.navy}` : highlighted ? `1.5px solid ${JAI.blue}` : `1px solid ${JAI.hair}`, boxShadow: highlighted && !disabled ? '0 16px 44px rgba(30,91,214,0.16), 0 2px 8px rgba(10,31,68,0.05)' : lift ? '0 12px 32px rgba(10,31,68,0.10)' : '0 2px 10px rgba(10,31,68,0.05)', padding: '26px 24px 24px', transform: lift ? 'translateY(-3px)' : 'translateY(0)', transition: 'all 0.22s ease' }}>
      {isCurrent ? <CurrentPlanBadge /> : plan.badge && !disabled && (
        <span style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: `linear-gradient(135deg, ${JAI.blueBright}, ${JAI.blue})`, color: 'white', fontSize: 11, fontWeight: 700, fontFamily: FONTS.tight, letterSpacing: '0.04em', borderRadius: 999, padding: '5px 13px', whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(30,91,214,0.35)' }}>{plan.badge}</span>
      )}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 17, fontWeight: 750, color: JAI.navy, fontFamily: FONTS.tight, letterSpacing: '-0.02em' }}>{plan.name}</div>
        <div style={{ fontSize: 12.5, color: JAI.faint, fontFamily: FONTS.tight, marginTop: 3 }}>{plan.tagline}</div>
      </div>
      <PriceBlock plan={plan} billing={billing} />
      <div style={{ minHeight: 18, marginTop: 6 }}>
        {plan.trialDays && (
          <span style={{ fontSize: 12, fontWeight: 600, color: JAI.success, fontFamily: FONTS.tight }}>{plan.trialDays}-day free trial</span>
        )}
      </div>
      <ul style={{ listStyle: 'none', margin: '14px 0 22px', padding: 0, display: 'flex', flexDirection: 'column', gap: 9, flex: 1 }}>
        {plan.features.map((f, i) => <FeatureLine key={i} muted={i === 0 && f.startsWith('Everything')}>{f}</FeatureLine>)}
      </ul>
      {disabled ? (
        <DisabledButton>{state.cta}</DisabledButton>
      ) : highlighted ? (
        <PrimaryButton type="button" loading={loading} onClick={() => onSelect(plan)}>{state.cta}</PrimaryButton>
      ) : (
        <SecondaryButton loading={loading} onClick={() => onSelect(plan)}>{state.cta}</SecondaryButton>
      )}
      {state.note && (
        <p style={{ margin: '9px 0 0', fontSize: 11.5, lineHeight: 1.45, color: JAI.faint, fontFamily: FONTS.tight }}>
          {state.note}
        </p>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// PLAN PICKER
// ════════════════════════════════════════════════════════════
export default function PlanPicker({ billing, setBilling, onSelect, busyPlanId, currentPlan = null, currentInterval = null, hasScheduledChange = false }) {
  const subscribed = !!currentPlan && currentPlan !== 'starter'
  return (
    <div style={{ width: '100%', maxWidth: 980, margin: '0 auto', animation: 'jaiSlideUp 0.4s ease both' }}>
      <div style={{ textAlign: 'center', marginBottom: 30 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          <JAIMascot size={64} state="excited" />
        </div>
        <h1 style={{ fontSize: 'clamp(32px, 4.4vw, 44px)', fontWeight: 880, fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.045em', lineHeight: 0.98, color: JAI.navy, margin: 0, fontFamily: FONTS.tight }}>
          {subscribed ? 'Change your plan' : 'Choose your plan'}
        </h1>
        <p style={{ fontSize: 14, color: JAI.mute, margin: '10px auto 0', maxWidth: 420, lineHeight: 1.55, fontFamily: FONTS.tight }}>
          {subscribed
            ? "Your current plan is marked below. Upgrades start straight away; moving down a tier or to monthly billing takes effect at your next renewal."
            : 'Pick the plan that fits how you play. Upgrade, downgrade, or cancel anytime.'}
        </p>
        <div style={{ marginTop: 20 }}>
          <BillingToggle billing={billing} onChange={setBilling} />
        </div>
        <p style={{ fontSize: 11.5, fontWeight: 600, color: JAI.faint, fontFamily: FONTS.tight, margin: '10px 0 0' }}>
          Billed in USD
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 18, alignItems: 'stretch' }}>
        {PLANS.map((p) => {
          const state = planCardState(p, { currentPlan, currentInterval, billing, hasScheduledChange })
          // "Current" means this exact tier AND this exact billing cycle — a
          // Pro monthly subscriber looking at the annual view is not looking
          // at their current plan, they're looking at the switch.
          const isCurrent = subscribed && p.id === currentPlan && (!currentInterval || billing === currentInterval)
          return (
            <PlanCard
              key={p.id}
              plan={p}
              billing={billing}
              highlighted={!!p.badge && !state.disabled}
              onSelect={onSelect}
              loading={busyPlanId === p.id}
              state={state}
              isCurrent={isCurrent}
            />
          )
        })}
      </div>

      <p style={{ textAlign: 'center', fontSize: 12.5, lineHeight: 1.6, color: JAI.mute, fontFamily: FONTS.tight, margin: '22px auto 0', maxWidth: 560 }}>
        {CREDITS_EXPLAINER} Run out? Buy a top-up on this page, or move up a plan.
      </p>

      {/* Pre-purchase disclosure of the auto-renewal terms.
        *
        * This has to be here, in plain sight, BEFORE the user hands over card
        * details — US ROSCA and California's Automatic Renewal Law both require
        * the recurring nature, the amount, the frequency, and how to cancel to
        * be disclosed up front, not buried in the Terms. Stripe Checkout repeats
        * the price and interval on the next screen, but the disclosure duty
        * starts on this page. Keep it visible; don't shrink it into a footnote
        * nobody reads. */}
      <div style={{ textAlign: 'center', marginTop: 26, maxWidth: 620, marginLeft: 'auto', marginRight: 'auto' }}>
        {subscribed ? (
          // Someone changing plan is not starting a trial, and saying so here
          // would be wrong. What they need instead is the promise that nothing
          // happens until they have seen the exact charge and confirmed.
          <p style={{ fontSize: 12.5, lineHeight: 1.65, color: JAI.faint, fontFamily: FONTS.tight, margin: 0 }}>
            Changing plan never starts a new trial. Before anything changes you'll see exactly what you'll be
            charged, and when, and you'll confirm it. Cancel any time from <strong>Settings → Cancel plan</strong>;
            you'll keep access until the end of the period you've paid for.
          </p>
        ) : (
          <p style={{ fontSize: 12.5, lineHeight: 1.65, color: JAI.faint, fontFamily: FONTS.tight, margin: 0 }}>
            Paid plans include a <strong>7-day free trial</strong> — you're not charged during the trial, and
            we'll email you before it ends. Unless you cancel first, your trial converts to a paid subscription
            at the price shown above and renews automatically{' '}
            {billing === 'annual' ? 'every year' : 'every month'} until you cancel. Cancel any time in a couple
            of clicks from <strong>Settings → Cancel plan</strong>; you'll keep access until the end of the
            period you've paid for.
          </p>
        )}
      </div>
    </div>
  )
}
